import React, { useState, useEffect } from "react";
import { GetData, DeleteData } from "../../services/api";
import DataTable from "react-data-table-component";
import moment from "moment";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Container,
  Row,
  Col,
  Button,
  Badge
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import Swal from 'sweetalert2';
import ReactPaginate from 'react-paginate';

const Campaigns = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [itemOffset, setItemOffset] = useState(0);
  const itemsPerPage = 10;
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = data.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(data.length / itemsPerPage);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % data.length;
    setItemOffset(newOffset);
  };

  const fetchData = async () => {
    try {
      const { data } = await GetData("/campaigns");
      const ordered = data.sort((a, b) => { return new Date(b.createdAt) - new Date(a.createdAt) });
      setData(ordered);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching campaigns:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { color: "secondary", text: "Borrador" },
      scheduled: { color: "info", text: "Programada" },
      sending: { color: "warning", text: "Enviando" },
      sent: { color: "success", text: "Enviada" },
      cancelled: { color: "danger", text: "Cancelada" },
    };
    const statusInfo = statusMap[status] || { color: "secondary", text: status };
    return <Badge color={statusInfo.color}>{statusInfo.text}</Badge>;
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Nombre</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Tipo</span>,
      selector: (row) => {
        const typeMap = {
          email: "Email",
          promotion: "Promoción",
          newsletter: "Newsletter",
        };
        return typeMap[row.type] || row.type;
      },
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Estado</span>,
      cell: (row) => getStatusBadge(row.status),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Enviados</span>,
      selector: (row) => row.sentCount || 0,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Abiertos</span>,
      selector: (row) => row.openedCount || 0,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Clickeados</span>,
      selector: (row) => row.clickedCount || 0,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Programada</span>,
      selector: (row) => row.scheduledAt ? moment(row.scheduledAt).format("DD/MM/YYYY HH:mm") : "-",
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Acción</span>,
      cell: (row) => {
        return (
          <UncontrolledDropdown className="dropdown d-inline-block">
            <DropdownToggle
              className="btn btn-soft-secondary btn-sm"
              tag="button"
            >
              <i className="ri-more-fill align-middle"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem>
                <Link to={`/campaigns/${row.id}`} className="text-muted">
                  <i className="ri-eye-fill align-bottom me-2 text-muted"></i>
                  Ver Detalle
                </Link>
              </DropdownItem>
              <DropdownItem>
                <Link to={`/campaigns/${row.id}/edit`} className="text-muted">
                  <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
                  Editar
                </Link>
              </DropdownItem>
              {row.status === 'draft' && (
                <DropdownItem onClick={() => handleSend(row.id)}>
                  <i className="ri-send-plane-fill align-bottom me-2 text-muted"></i>
                  Enviar
                </DropdownItem>
              )}
              <DropdownItem className="remove-item-btn" onClick={() => handleDelete(row.id)}>
                <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>
                Eliminar
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
  ];

  const handleSend = async (id) => {
    try {
      // TODO: Implement send campaign API call
      Swal.fire({
        title: "Campaña enviada",
        icon: "success"
      });
      fetchData();
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Error al enviar la campaña",
        icon: "error"
      });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "¿Deseas eliminar la campaña?",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#dc3545'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await DeleteData(`/campaigns/${id}`);
          Swal.fire("Campaña eliminada!", "", "success");
          fetchData();
        } catch (err) {
          Swal.fire({
            title: "Error",
            text: err.message || "Error al eliminar la campaña",
            icon: "error"
          });
        }
      }
    });
  };

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs title="IHubG" breadcrumbItem="Campañas" />
        <Row className="my-4">
          <Col className="col-sm-auto">
            <div className="d-flex gap-1 justify-content-end">
              <Button
                color="success"
                className="add-btn"
                onClick={() => navigate("/campaigns/create")}
              >
                <i className="ri-add-line align-bottom me-1"></i> Nueva Campaña
              </Button>
            </div>
          </Col>
        </Row>
        <Row className="mb-4">
          {loading ? (
            <Loader size={150} />
          ) : (
            <>
              <DataTable
                data={currentItems}
                columns={columns}
                noDataComponent={<span className="py-4">Sin campañas. Crea tu primera campaña.</span>}
              />
              <ReactPaginate
                breakLabel="..."
                nextLabel="Sig. >"
                onPageChange={handlePageClick}
                pageRangeDisplayed={10}
                pageCount={pageCount}
                previousLabel="< Ant."
                renderOnZeroPageCount={null}
                className='reactPaginate'
                activeClassName="reactPaginate-active"
              />
            </>
          )}
        </Row>
      </Container>
    </div>
  );
};

export default Campaigns;

